import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePerson } from '../../contexts/PersonContext'
import { useOpenRooms } from '../../hooks/useOpenRooms'
import { StartRoomDialog } from './StartRoomDialog'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import { EmptyState } from '../ui/EmptyState'
import { Pill } from '../ui/Pill'
import { SectionLabel } from '../ui/SectionLabel'

/** Shared room discovery and hosting for guardians and eligible players. */
export function OpenRoomsSection() {
  const { activePerson, flags, role, guardianFirst } = usePerson()
  const query = useOpenRooms(activePerson?.id)
  const navigate = useNavigate()
  const [startOpen, setStartOpen] = useState(false)
  const canHost = role === 'parent' || (!flags.supervised && !flags.viewOnly)
  const rooms = query.data ?? []

  return <section className="mt-8" aria-label="Rooms open now">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <SectionLabel>Rooms open now</SectionLabel>
      {canHost && <Button variant="ink" onClick={() => setStartOpen(true)}>Start a room</Button>}
    </div>
    {query.isLoading ? <p role="status" className="mt-3 text-sm text-ink-muted">Loading rooms…</p>
      : query.isError ? <div role="alert" className="mt-3 rounded-btn border border-line p-4">
        <p>Rooms could not be loaded. Check your connection and try again.</p>
        <Button variant="outline" className="mt-3" onClick={() => void query.refetch()}>Retry rooms</Button>
      </div>
      : rooms.length === 0 ? <EmptyState dashed className="mt-3" title="No rooms open right now"
        description={canHost ? 'Start one from one of your circles.' : `${guardianFirst || 'A grown-up'} or a circle host can start one.`} />
      : <div className="mt-3 grid gap-4 sm:grid-cols-2">
        {rooms.map(room => <Card key={room.id} padding="none" className="min-w-0 overflow-hidden">
          <div className="h-[5px]" style={{ background: room.tint || '#b9704f' }} />
          <div className="p-5">
            <SectionLabel>{room.game}</SectionLabel>
            <p className="mt-2 text-sm font-medium text-ink">{room.hostLine}</p>
            {room.blurb && <p className="mt-1 break-words text-sm text-ink-muted">{room.blurb}</p>}
            <div className="mt-3 flex flex-wrap gap-2"><Pill>{room.slotsText}</Pill>{room.modeText && <Pill tone="moss">{room.modeText}</Pill>}</div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <Button variant="ink" size="sm" onClick={() => navigate(`/rooms/${room.id}`)}>Open room</Button>
              <span className="break-all rounded-btn border border-dashed border-line px-3 py-2 font-mono text-[11px]">{room.code}</span>
            </div>
          </div>
        </Card>)}
      </div>}
    {canHost && <StartRoomDialog open={startOpen} onClose={() => setStartOpen(false)} onCreated={id => { setStartOpen(false); navigate(`/rooms/${id}`) }} />}
  </section>
}
